import { useCallback } from 'react';
import tw from 'twin.macro';
import { useTranslation } from 'react-i18next';

import { download } from '@utils/download';
import { ExportIcon } from '@icons/ExportIcon';
import { Loader } from '@components/Loader';
import { Button } from '@components/Button';
import { useInsightsContext } from '@insights_module/context/InsightsContext';
import { useEpisodeDetailsContext } from '@insights_module/context/EpisodeDetailsContext';
import { EpisodeDetailsInfoBadge } from '@insights_module/components/episode_details_screen/EpisodeDetailsItemInfoBadge';
import { useEpisodeDetailsData } from '@insights_module/hooks/useEpisodeDetailsData';

const ContentWrapper = tw.div`flex px-[24px] py-[16px] items-start content-start flex-wrap gap-[12px]`;
const LoaderWrapper = tw.div`flex justify-center items-center h-[200px] w-full`;
const DownloadButton = tw(
  Button.Primary
)`flex justify-center items-center min-h-[40px] px-[14px] bg-accent-2 text-text-and-icon-invert`;

export const EpisodesTopicsItem = () => {
  const { selectedEpisodeDetails, isLoading } = useEpisodeDetailsData();
  const { activeView } = useEpisodeDetailsContext();

  if (activeView !== 'topics') return null;

  return (
    <ContentWrapper>
      {isLoading && (
        <LoaderWrapper>
          <Loader size='lg' theme='dark' />
        </LoaderWrapper>
      )}
      {selectedEpisodeDetails?.topics?.map((item) => (
        <EpisodeDetailsInfoBadge
          key={item.topic}
          label={item.topic}
          value={item.score.toString()}
        />
      ))}
    </ContentWrapper>
  );
};

export const DownloadTopicsButton = () => {
  const { t } = useTranslation();
  const { selectedEpisode } = useInsightsContext();
  const { selectedEpisodeDetails, isLoading } = useEpisodeDetailsData();
  const { activeView } = useEpisodeDetailsContext();

  const handleClickDownload = useCallback(() => {
    if (isLoading || !selectedEpisodeDetails?.topics) return;

    const file = new Blob(
      [selectedEpisodeDetails.topics.map((item) => `${item.topic} ${item.score}`).join('\n')],
      {
        type: 'text/plain',
      }
    );
    const fileName = `topics-${selectedEpisode}.txt`;

    download(file, fileName);
  }, [selectedEpisodeDetails?.topics]);

  if (activeView !== 'topics') return null;

  return (
    <DownloadButton
      onClick={handleClickDownload}
      disabled={isLoading || !selectedEpisodeDetails?.topics?.length}>
      <ExportIcon tw='stroke-text-and-icon-invert' />
      {t('insights.episode_details_drawer.download_button.topics')}
    </DownloadButton>
  );
};
